'use client';
/**
 * Chantier manuel-vitrine, lot V5 — le CIRCUIT ÉDITORIAL du catalogue, tenu
 * depuis Qualité.
 *
 * L'écran montre ce que le valideur DOIT voir : chaque proposition avec son
 * diff champ par champ contre la fiche publiée — le défaut historique du
 * circuit était précisément une modale de diff qui ne montrait rien. Les
 * transitions passent par le moteur (`ged/manuel-circuit/*`), qui porte les
 * gardes que l'ancienne UI promettait sans les tenir : rôle `rq` pour valider
 * et diffuser, refus de valider sa propre proposition, aucune diffusion avant
 * la date d'application.
 *
 * La rédaction de FOND d'une fiche se fait dans la fiche elle-même (l'unique
 * éditeur) ; cet écran pilote le CIRCUIT — proposer, relire, valider, refuser,
 * diffuser — c'est-à-dire ce qui engage le document opposable.
 *
 * Depuis le 18/10/2026 : la FICHE de l'examen porte elle aussi ces actes
 * (bandeau en tête, `CircuitFiche.tsx`) et montre le brouillon ligne à ligne ;
 * cet écran reste la vue d'ensemble — tout ce qui attend dans le laboratoire,
 * et la diffusion groupée.
 */
import React, { useEffect, useState } from 'react';
import { Tableau, ligneOuvrable } from '@/shared/Tableau';
import { fmtDate } from '@/lib/meta';
import { card, EmptyState, Skeleton, useConfirmation, Pastille } from '@/components/ui';
import { useUrlsModules } from '@/shared/urlsModulesClient';
import { moteur } from './Registres';
import { useGed } from '@/lib/store';

import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
import { PiedActions } from '@/shared/PiedActions';
type Diff = { champ: string; avant: unknown; apres: unknown };
type Proposition = {
  documentId: string; code: string;
  statut: 'brouillon' | 'propose' | 'valide' | 'rejete';
  author: string | null; date: string | null; dateApp: string | null;
  observation: string | null; indiffusables: string[]; diff: Diff[];
  /** Les champs opposables touchés — vide = correction simple (18/10/2026). */
  opposables?: string[];
};
type Etat = { propositions: Proposition[]; aujourdhui: string };

const badge = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-block', padding: '1px 8px', borderRadius: 'var(--radius-md)', fontSize: 11, fontWeight: 700,
  background: bg, color: fg,
});
const BADGES: Record<Proposition['statut'], React.CSSProperties> = {
  brouillon: badge('var(--gray-100)', 'var(--gray-700)'),
  propose: badge('var(--amber-50)', 'var(--amber-600)'),
  valide: badge('var(--cyan-50)', 'var(--cyan-700)'),
  rejete: badge('var(--red-50)', 'var(--red-600)'),
};
const valeurCourte = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 90 ? `${s.slice(0, 90)}…` : s;
};

/** LES ACTES d'une proposition — la colonne « Actions » du registre (L7 vague 2, 11/10/2026). */
function ActesProposition({ p, moi, estRq, onFait, onErreur }: {
  p: Proposition; moi: string; estRq: boolean; onFait: () => void; onErreur: (m: string) => void;
}) {
  /* OUVRIR LA FICHE : depuis le 18/10/2026 la fiche de l'examen est l'unique
     éditeur et porte elle-même les actes du circuit. Poser son code dans
     `selectedRef` suffit : l'écran du référentiel l'ouvre, et l'adresse suit
     (`/manuel-prelevement/<CODE>`). */
  const select = useGed((s) => s.select);
  const { demander, demanderTexte, confirmation } = useConfirmation();
  const [envoi, setEnvoi] = useState(false);
  const simple = (p.opposables || []).length === 0 && p.diff.length > 0;

  const transition = (action: string, corps?: unknown) => {
    setEnvoi(true);
    void moteur(`manuel-circuit/${p.documentId}/${action}`, corps ?? {}).then((r) => {
      setEnvoi(false);
      if (!r.ok) { onErreur(r.message); return; }
      onFait();
    });
  };
  const valider = async () => {
    const dateApp = p.dateApp || await demanderTexte('Date d’application — elle borne la diffusion :',
      { titre: 'Approuver la proposition', confirmer: 'Approuver', type: 'date' });
    if (!dateApp) return;
    /* `diffLu` (14/10/2026) : ce que la RQ a relu. Si la proposition a bougé
       depuis, le moteur refuse (409) au lieu d'approuver un contenu que
       personne n'a vu. Un moteur antérieur ne lit que `dateApp`. */
    transition('valider', { dateApp, diffLu: p.diff });
  };
  const refuser = async () => {
    const observation = await demanderTexte('Motif du refus (obligatoire — il est journalisé) :',
      { titre: 'Refuser la proposition', confirmer: 'Refuser' });
    if (!observation) return;
    transition('refuser', { observation });
  };
  // La séparation des tâches se MONTRE : le bouton absent avec sa raison vaut
  // mieux qu'un 403 surprise après le clic.
  const estAuteur = !!p.author && p.author === moi;
  const { manuel: MANUEL_URL } = useUrlsModules();

  return (
    <>
      {/* Deux vitesses (18/10/2026) : une correction simple se diffuse par le RQ
          seul, depuis la liste comme depuis la fiche. */}
      {(p.statut === 'brouillon' || (p.statut === 'propose' && estAuteur)) && simple && estRq && (
        <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={async () => {
          const ok = await demander(`Diffuser cette correction sur ${p.code} sans seconde relecture ? La fiche publique change tout de suite ; le journal l’inscrit à votre nom.`,
            { titre: 'Diffuser la correction', confirmer: 'Oui, diffuser' });
          if (ok) transition('corriger');
        }}>Diffuser la correction</Bouton>
      )}
      {p.statut === 'brouillon' && (
        <Bouton taille="sm" disabled={envoi} onClick={() => transition('proposer')}>Proposer</Bouton>
      )}
      {p.statut === 'propose' && estRq && !estAuteur && (
        <>
          <Bouton variante="primaire" taille="sm" disabled={envoi} onClick={valider}>Approuver</Bouton>
          <Bouton variante="danger" taille="sm" disabled={envoi} onClick={refuser}>Refuser</Bouton>
        </>
      )}
      {p.statut === 'propose' && estRq && estAuteur && (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>votre proposition — un autre RQ relit</span>
      )}
      {p.statut === 'valide' && estRq && (
        <Bouton taille="sm" disabled={envoi} onClick={() => transition('devalider')}>Dévalider</Bouton>
      )}
      {/* LA FICHE, où tout se lit et se modifie. Le formulaire « Modifier »
          qui vivait ici (troisième éditeur des mêmes champs) est retiré le
          18/10/2026 : la fiche montre le brouillon ligne à ligne, et c'est là
          qu'on corrige. */}
      <Bouton taille="sm" title="Ouvrir la fiche de l’examen — le brouillon s’y lit ligne à ligne et s’y corrige"
        onClick={() => select('selectedRef', p.code)}>
        Ouvrir la fiche
      </Bouton>
      {/* La fiche telle que le public la lit, en second recours. */}
      <a href={`${MANUEL_URL}/fiche/${encodeURIComponent(p.code)}?apercu=public`}
        target="_blank" rel="noopener noreferrer"
        title="Voir la fiche publique"
        className="ged-btn"
        style={{ ...styleBouton('contour', 'sm'), textDecoration: 'none' }}>
        Voir ↗
      </a>
      {confirmation}
    </>
  );
}

/** LE DÉTAIL déplié d'une proposition : indiffusables, observation, et le diff champ par champ. */
function DetailProposition({ p }: { p: Proposition }) {
  return (
    <div style={{ padding: 'var(--tableau-ligne-padding-y) var(--tableau-ligne-padding-x)', background: 'var(--gray-50)' }}>
      {p.indiffusables.length > 0 && (
        <div style={{ color: 'var(--red-600)', fontSize: 12.5, marginBottom: 6 }}>
          ⚠ Champs non diffusables : {p.indiffusables.join(', ')} — la diffusion sera refusée tant qu&rsquo;ils restent.
        </div>
      )}
      {p.observation && <div style={{ fontSize: 12.5, marginBottom: 6 }}>Observation : {p.observation}</div>}
      {/* Le diff, au niveau SIMPLE : trois colonnes, l'ordre des champs est celui
          de la proposition tant que personne ne trie. */}
      <Tableau
        id="manuel-circuit-diff"
        ajusterLargeur
        niveau="simple"
        lignes={p.diff}
        cleLigne={(d) => d.champ}
        vide={{ titre: 'Aucune différence avec la fiche publiée.' }}
        colonnes={[
          { cle: 'champ', libelle: 'Champ', valeur: (d) => d.champ, rendu: (d) => <strong>{d.champ}</strong> },
          { cle: 'avant', libelle: 'Publié', valeur: (d) => valeurCourte(d.avant), rendu: (d) => valeurCourte(d.avant) },
          { cle: 'apres', libelle: 'Proposé', valeur: (d) => valeurCourte(d.apres), rendu: (d) => valeurCourte(d.apres) },
        ]}
      />
    </div>
  );
}

export function OngletCircuit({ estRq }: { estRq: boolean }) {
  const [d, setD] = useState<Etat | null>(null);
  const [moi, setMoi] = useState('');
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [version, setVersion] = useState(0);
  const [envoi, setEnvoi] = useState(false);
  /* Les fiches RETENUES pour la diffusion (retour Ines du 17/08).
     `null` = « toutes les validées », l'état de départ : c'est le cas
     courant, et exiger de tout cocher pour le geste ordinaire ferait
     perdre du temps à chaque diffusion. Dès qu'on décoche, la sélection
     devient explicite et le bouton dit combien partent. */
  const [retenues, setRetenues] = useState<string[] | null>(null);
  /* La diffusion attend confirmation — `false` tant qu'elle n'est pas demandée. */
  const [confirmer, setConfirmer] = useState(false);
  /* Les propositions DÉPLIÉES (détail sous la ligne) : l'état vit ici, le
     socle rend les lignes. */
  const [ouvertes, setOuvertes] = useState<Set<string>>(new Set());
  const basculerDetail = (id: string) => setOuvertes((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const recharger = () => { setD(null); setErreur(''); setVersion((v) => v + 1); };

  useEffect(() => {
    let vivant = true;
    void moteur('manuel-circuit/etat').then((r) => {
      if (!vivant) return;
      if (!r.ok) { setErreur(r.message); return; }
      setD(r.data as unknown as Etat);
    });
    void fetch('/api/ged/ged/me', { credentials: 'include' })
      .then((res) => res.json())
      .then((me) => { if (vivant) setMoi(String(me?.name || me?.key || '')); })
      .catch(() => {});
    return () => { vivant = false; };
  }, [version]);

  if (erreur && !d) return <EmptyState titre="Circuit indisponible" indice={erreur} action={<Bouton taille="sm" type="button" onClick={recharger}>Réessayer</Bouton>} />;
  if (!d) return <Skeleton rows={6} />;

  const valides = d.propositions.filter((p) => p.statut === 'valide');

  /* La SÉLECTION est respectée (RQ, 19/08/2026) : le corps posté PORTE la
     liste cochée, plutôt qu'un corps vide qui vaudrait « toutes ». */
  const diffuser = () => {
    setEnvoi(true); setMessage('');
    void moteur('manuel-circuit/diffuser', { codes: retenues ?? valides.map((p) => p.code) }).then((r) => {
      setEnvoi(false);
      if (!r.ok) { setErreur(r.message); return; }
      const data = r.data as { diffusees: string[]; echecs: string[] };
      setMessage(`${data.diffusees.length} fiche(s) diffusée(s)${data.echecs.length ? ` · échecs : ${data.echecs.join(' ; ')}` : ''}`);
      recharger();
    });
  };

  return (
    <div>
      {erreur && <div style={{ color: 'var(--red-600)', fontSize: 12.5, marginBottom: 6 }}>{erreur}</div>}
      {message && <div style={{ color: 'var(--green-600)', fontSize: 12.5, marginBottom: 6 }}>{message}</div>}

      {/* L7 vague 2 (11/10/2026) : registre STANDARD sur le socle. La ligne
          déplie le détail (indiffusables, observation, diff) — É-5 : au clavier
          aussi ; les actes sont en colonne d'actions ; l'édition du fond s'ouvre
          en fenêtre, hors du tableau. */}
      <Tableau
        id="manuel-circuit"
        ajusterLargeur
        niveau="standard"
        lignes={d.propositions}
        cleLigne={(p) => p.documentId}
        ligneProps={(p) => ({
          ...ligneOuvrable(() => basculerDetail(p.documentId), `${ouvertes.has(p.documentId) ? 'Replier' : 'Déplier'} ${p.code}`),
          'aria-expanded': ouvertes.has(p.documentId),
        })}
        vide={{
          titre: 'Aucune proposition en circuit',
          indice: 'Le catalogue publié est à jour de tout ce qui a été proposé. Une modification naît dans la fiche d’un examen : cliquez une valeur, enregistrez — elle arrive ici.',
        }}
        colonnes={[
          { cle: 'code', libelle: 'Examen', valeur: (p) => p.code, rendu: (p) => <strong>{p.code}</strong> },
          { cle: 'statut', libelle: 'Statut', type: 'liste', valeur: (p) => p.statut, rendu: (p) => <span style={BADGES[p.statut]}>{p.statut}</span> },
          {
            /* Deux vitesses : « simple » se diffuse par le RQ seul, « double »
               exige un autre RQ et une date. Le moteur décide, la colonne lit. */
            cle: 'relecture', libelle: 'Relecture', type: 'liste',
            valeur: (p) => ((p.opposables || []).length ? 'double' : 'simple'),
            rendu: (p) => ((p.opposables || []).length
              ? <Pastille compacte ton="warning" title={`Champs opposables : ${(p.opposables || []).join(', ')}`}>double</Pastille>
              : <Pastille compacte ton="neutral" title="Aucun champ opposable : le RQ diffuse seul">simple</Pastille>),
          },
          {
            cle: 'author', libelle: 'Auteur', valeur: (p) => p.author || '',
            rendu: (p) => <>{p.author || '—'}<div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{p.date || ''}</div></>,
          },
          { cle: 'dateApp', libelle: 'Date d’application', type: 'date', valeur: (p) => p.dateApp || '', rendu: (p) => (p.dateApp ? fmtDate(p.dateApp) : '—') },
          { cle: 'diff', libelle: 'Diff', type: 'nombre', valeur: (p) => p.diff.length, rendu: (p) => `${p.diff.length} champ(s)` },
        ]}
        actions={(p) => (
          <ActesProposition p={p} moi={moi} estRq={estRq} onFait={recharger} onErreur={setErreur} />
        )}
        detail={(p) => (ouvertes.has(p.documentId) ? <DetailProposition p={p} /> : null)}
      />
      {estRq && valides.length > 0 && (
        <div style={{ ...card, padding: 12, marginTop: 10, background: 'var(--cyan-50)' }}>
          <strong style={{ fontSize: 12.5 }}>{valides.length} fiche(s) validée(s)</strong> — prêtes à diffuser.

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            {valides.map((p) => {
              const prise = (retenues ?? valides.map((x) => x.code)).includes(p.code);
              return (
                <label key={p.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, background: prise ? 'var(--surface-card)' : 'transparent', border: `1px solid ${prise ? 'var(--color-primary)' : 'var(--border-default)'}`, borderRadius: 'var(--radius-pill)', padding: '3px 11px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={prise}
                    onChange={(e) => {
                      const base = retenues ?? valides.map((x) => x.code);
                      setRetenues(e.currentTarget.checked ? [...base, p.code] : base.filter((c) => c !== p.code));
                    }} />
                  <span style={{ fontFamily: 'var(--font-mono)' }}>{p.code}</span>
                </label>
              );
            })}
            {retenues !== null && (
              <Bouton taille="sm" type="button" onClick={() => setRetenues(null)}>Tout reprendre</Bouton>
            )}
          </div>

          <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.6, marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--cyan-200)' }}>
            Ce bouton <strong>publie pour de bon</strong> : la fiche publique change immédiatement, et
            l’ancienne valeur n’est plus celle qui fait foi. La version de chaque examen (et de son
            paramètre, s’il en porte un) monte au chiffre entier suivant — c’est cette diffusion qui
            distingue une v4 « en travail » (4.1, 4.2…) d’une v5 devenue opposable.
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <Bouton variante="primaire" disabled={envoi || (retenues !== null && retenues.length === 0)} onClick={() => setConfirmer(true)}>
              Diffuser ({(retenues ?? valides.map((x) => x.code)).length} fiche(s))
            </Bouton>
          </div>

          {confirmer && (
            <div style={{ ...card, padding: 12, marginTop: 10, borderColor: 'var(--amber-500)' }}>
              <strong style={{ fontSize: 12.5 }}>Diffuser</strong>
              <div style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.55 }}>
                La fiche publique de <strong>{(retenues ?? valides.map((x) => x.code)).join(', ')}</strong>
                {' '}va changer <strong>tout de suite</strong>.
              </div>
              <PiedActions taille="sm" style={{ marginTop: 9 }} annuler={{ onClick: () => setConfirmer(false), disabled: envoi }} principale={{ libelle: "Oui, diffuser", disabled: envoi, onClick: () => { setConfirmer(false); diffuser(); } }} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
